import { api } from './api';
import type { ApiResponse } from '../types/index';
import type {
  DuesSetting,
  DuesAssessmentWithUser,
  DuesSummary,
  UserDuesStatus,
  BranchProgress,
  CreateDuesSettingRequest,
  UpdateDuesSettingRequest,
  UpdateAssessmentAmountRequest,
  BulkUpdateAssessmentAmountRequest,
  WaiveAssessmentRequest,
  AssessmentFilters,
} from '../types/payments';
import { buildQueryString } from '../utils/query-string';

export const duesService = {
  // Dues Settings
  async getDuesSettings(): Promise<ApiResponse<{ settings: DuesSetting[] }>> {
    return api.get<ApiResponse<{ settings: DuesSetting[] }>>('/dues/settings');
  },

  async createDuesSetting(data: CreateDuesSettingRequest): Promise<ApiResponse<{ setting: DuesSetting }>> {
    return api.post<ApiResponse<{ setting: DuesSetting }>>('/dues/settings', data);
  },

  async updateDuesSetting(id: string, data: UpdateDuesSettingRequest): Promise<ApiResponse<{ setting: DuesSetting }>> {
    return api.put<ApiResponse<{ setting: DuesSetting }>>(`/dues/settings/${id}`, data);
  },

  // User Dues Status
  async getMyDuesStatus(): Promise<ApiResponse<UserDuesStatus>> {
    return api.get<ApiResponse<UserDuesStatus>>('/dues/my-status');
  },

  // Admin Dues Management
  async getAdminDuesSummary(year?: number): Promise<ApiResponse<{ summary: DuesSummary }>> {
    const query = year ? `?year=${year}` : '';
    return api.get<ApiResponse<{ summary: DuesSummary }>>(`/dues/summary${query}`);
  },

  async getAssessmentsList(duesSettingId: string, filters?: AssessmentFilters): Promise<ApiResponse<{ assessments: DuesAssessmentWithUser[] }>> {
    const params: Record<string, unknown> = { ...filters, duesSettingId };
    const query = buildQueryString(params);
    return api.get<ApiResponse<{ assessments: DuesAssessmentWithUser[] }>>(`/dues/assessments${query}`);
  },

  async createBulkAssessments(data: {
    duesSettingId: string;
    userIds?: string[];
    familyMemberIds?: string[];
    sendNotification?: boolean;
  }): Promise<ApiResponse<{ created: number }>> {
    return api.post<ApiResponse<{ created: number }>>('/dues/assessments/bulk', data);
  },

  async updateAssessmentAmount(assessmentId: string, data: UpdateAssessmentAmountRequest): Promise<ApiResponse<{ assessment: DuesAssessmentWithUser }>> {
    return api.put<ApiResponse<{ assessment: DuesAssessmentWithUser }>>(`/dues/assessments/${assessmentId}/amount`, data);
  },

  async bulkUpdateAssessmentAmount(data: BulkUpdateAssessmentAmountRequest): Promise<ApiResponse<{ updated: number }>> {
    return api.put<ApiResponse<{ updated: number }>>('/dues/assessments/bulk-amount', data);
  },

  async waiveAssessment(assessmentId: string, data: WaiveAssessmentRequest): Promise<ApiResponse<{ assessment: DuesAssessmentWithUser }>> {
    return api.post<ApiResponse<{ assessment: DuesAssessmentWithUser }>>(`/dues/assessments/${assessmentId}/waive`, data);
  },

  // Branch Progress
  async getBranchProgress(): Promise<ApiResponse<{ year: number; branches: BranchProgress[] }>> {
    return api.get<ApiResponse<{ year: number; branches: BranchProgress[] }>>('/dues/branch-progress');
  },
};
